import type { EndDatePreset } from '@/components/ui/date-picker/date-range-picker'
import { FilterSelectAsync } from '@/components/ui/filters/filter-select-async'
import {
    DatacentralHookForm,
    type DatacentralHookFormDataItem,
} from '@/components/ui/form-field'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { PRODUCT_UNITS } from '@/modules/Contract/constants'
import { INVOICE_ITEM_DESCRIPTIONS } from '@/modules/Invoice/constants'
import {
    createInvoiceItem,
    updateInvoiceItem,
} from '@/modules/Invoice/services'
import { InvoiceItem } from '@/modules/Invoice/types'
import { getServiceByCustomerID } from '@/modules/Service/services'
import { ModalProps } from '@/types/shared'
import { toDate } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'vnetwork-ui'
import AddInvoiceItemsSchema from './schema'

export interface AddInvoiceItemsProps extends ModalProps {
    invoice_id: number
    customer_id: number
    service_id: number
    invoice_item?: InvoiceItem
}

export interface FormValuesAddInvoiceItem extends Partial<InvoiceItem> {
    auto_calculate: boolean
    is_add_discount: boolean
}

const INVOICE_END_DATE_PRESETS: EndDatePreset[] = [
    {
        name: '1month',
        label: '1 tháng',
        getEndDate: (s) => s.add(1, 'month').subtract(1, 'day').toDate(),
    },
    {
        name: '3months',
        label: '3 tháng',
        getEndDate: (s) => s.add(3, 'month').subtract(1, 'day').toDate(),
    },
    {
        name: 'endQuarter',
        label: 'Cuối quý',
        getEndDate: (s) => s.endOf('quarter' as any).toDate(),
    },
    {
        name: '6months',
        label: '6 tháng',
        getEndDate: (s) => s.add(6, 'month').subtract(1, 'day').toDate(),
    },
    {
        name: 'endYear',
        label: 'Cuối năm',
        getEndDate: (s) => s.endOf('year').toDate(),
    },
    {
        name: '1year',
        label: '1 năm',
        getEndDate: (s) => s.add(1, 'year').subtract(1, 'day').toDate(),
    },
]

const AddInvoiceItems: React.FC<AddInvoiceItemsProps> = ({
    invoice_id,
    onClose,
    open,
    customer_id,
    service_id,
    invoice_item,
}) => {
    const { t } = useTranslation()

    const formControl = useForm<FormValuesAddInvoiceItem>({
        defaultValues: invoice_item
            ? {
                  ...invoice_item,
                  auto_calculate: true,
                  tax_percentage: Math.round(
                      invoice_item?.tax_percentage * 100
                  ),
                  is_add_discount:
                      invoice_item?.invoice_item_description === 'discount',
              }
            : {
                  auto_calculate: true,
                  invoice_item_amount: 0,
                  invoice_item_name: '',
                  invoice_item_tags: '',
                  usage_quantity: 1,
                  usage_price: 0,
                  invoice_item_taxable: false,
                  tax_percentage: 10,
                  usage_unit: 'qty',
                  invoice_item_description: 'usage_parameters',
                  service_id,
                  invoice_item_discount: 0,
                  is_add_discount: false,
                  invoice_item_apply_startdate: null,
                  invoice_item_apply_enddate: null,
              },
        resolver: yupResolver(AddInvoiceItemsSchema as any),
    })

    // Auto-calculate amount when quantity, price, or discount changes
    const autoCalculate = formControl.watch('auto_calculate')
    const isDiscount = formControl.watch('is_add_discount')
    const quantity = formControl.watch('usage_quantity')
    const price = formControl.watch('usage_price')
    const discount = formControl.watch('invoice_item_discount')

    useEffect(() => {
        if (!autoCalculate || isDiscount) return
        const amount =
            (Number(quantity) || 0) * (Number(price) || 0) -
            (Number(discount) || 0)
        formControl.setValue('invoice_item_amount', amount)
    }, [autoCalculate, isDiscount, quantity, price, discount, formControl])

    // Real-time date range validation (full-month enforcement)
    const watchedStartDate = formControl.watch('invoice_item_apply_startdate')
    const watchedEndDate = formControl.watch('invoice_item_apply_enddate')

    useEffect(() => {
        if (!watchedStartDate || !watchedEndDate) return
        const s = dayjs(watchedStartDate)
        const e = dayjs(watchedEndDate)
        if (!s.isValid() || !e.isValid()) return

        if (s.date() === 1 && e.date() === e.daysInMonth()) {
            formControl.clearErrors('invoice_item_apply_startdate' as any)
            return
        }

        if (e.isBefore(s.add(1, 'month'))) {
            formControl.clearErrors('invoice_item_apply_startdate' as any)
            return
        }

        formControl.setError('invoice_item_apply_startdate' as any, {
            type: 'manual',
            message: t('date_range_must_be_full_months'),
        })
    }, [watchedStartDate, watchedEndDate, formControl, t])

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: createInvoiceItem },
        {
            reactQueryKeys: QUERY_KEYS.INVOICES,
            successMessage: t('create-success'),
            onSuccess: () => onClose(),
        }
    )

    const { mutateAsync: updateMutateAsync, isPending: isUpdatePending } =
        useMutationWithHandler(
            { mutationFn: updateInvoiceItem },
            {
                reactQueryKeys: QUERY_KEYS.INVOICES,
                successMessage: t('update-success'),
                onSuccess: () => onClose(),
            }
        )

    const handleSubmit = (values: FormValuesAddInvoiceItem) => {
        const serviceId =
            typeof values.service_id === 'object'
                ? (values.service_id as any)?.value
                : values.service_id
        const payload = {
            ...values,
            invoice_id,
            service_id: serviceId,
            tax_percentage: values.tax_percentage
                ? values.tax_percentage / 100
                : 0,
            invoice_item_apply_startdate: toDate(
                values?.invoice_item_apply_startdate
            ),
            invoice_item_apply_enddate: toDate(
                values?.invoice_item_apply_enddate
            ),
            invoice_item_amount: Math.round(
                values?.is_add_discount
                    ? -(values.invoice_item_discount as number)
                    : (values.invoice_item_amount as number)
            ),
            invoice_item_taxable: values.invoice_item_taxable || false,
        }

        invoice_item ? updateMutateAsync(payload) : mutateAsync(payload)
    }

    const isLoading = isPending || isUpdatePending

    const formData: DatacentralHookFormDataItem<FormValuesAddInvoiceItem>[] = [
        {
            formItemProps: {
                name: 'auto_calculate',
                label: t('auto-calculate'),
                type: 'checkbox',
                bordered: true,
            },
            colSpan: 2,
        },
        {
            formItemProps: {
                name: 'is_add_discount',
                type: 'checkbox',
                label: t('add-discount'),
                bordered: true,
            },
            colSpan: 2,
        },
        {
            renderEditComponent: (control) => (
                <FilterSelectAsync
                    control={control}
                    name="service_id"
                    label={t('contract_service')}
                    placeholder={t('contract_service')}
                    disabled={!customer_id}
                    asyncOptions={(q) =>
                        getServiceByCustomerID({
                            t,
                            payload: {
                                page: 1,
                                page_size: (q?.length || 0) + 5,
                                q,
                            },
                            customerID: Number(customer_id),
                        })
                    }
                    getDefaultOptions={() =>
                        getServiceByCustomerID({
                            t,
                            payload: {
                                page: 1,
                                page_size: 10,
                                q: '',
                            },
                            customerID: Number(customer_id),
                        })
                    }
                />
            ),
            colSpan: 4,
        },
        {
            formItemProps: {
                name: 'invoice_item_name',
                label: t('invoice-item-name'),
                placeholder: t('invoice-item-name'),
                type: 'textfield',
            },
            colSpan: 4,
        },
        {
            formItemProps: {
                name: 'invoice_item_description',
                label: t('description'),
                placeholder: t('invoice_item_description'),
                type: 'select',
                options: INVOICE_ITEM_DESCRIPTIONS(t),
            },
            colSpan: 4,
        },
        {
            formItemProps: {
                name: 'invoice_item_tags',
                label: t('invoice_item_tags'),
                placeholder: t('invoice_item_tags'),
                type: 'textarea',
            },
            colSpan: 4,
        },
        {
            formItemProps: {
                name: 'usage_quantity',
                label: t('usage_quantity'),
                placeholder: t('usage_quantity'),
                type: 'number',
            },
            hidden: isDiscount,
            colSpan: 2,
        },
        {
            formItemProps: {
                name: 'usage_price',
                label: t('usage_price'),
                placeholder: t('usage_price'),
                type: 'number',
            },
            hidden: isDiscount,
            colSpan: 2,
        },
        {
            formItemProps: {
                name: 'invoice_item_discount',
                label: t('invoice_item_discount'),
                placeholder: t('invoice_item_discount'),
                type: 'number',
            },
            colSpan: 2,
        },
        {
            formItemProps: {
                name: 'invoice_item_amount',
                label: t('invoice_item_amount'),
                placeholder: t('invoice_item_amount'),
                type: 'number',
                disabled: isDiscount,
            },
            colSpan: 2,
        },
        {
            formItemProps: {
                name: 'invoice_item_taxable',
                label: isDiscount
                    ? t('invoice_item_taxable_discount')
                    : t('invoice_item_taxable'),
                type: 'checkbox',
                bordered: true,
            },
            colSpan: 2,
        },
        {
            formItemProps: {
                name: 'tax_percentage',
                label: 'VAT (%)',
                placeholder: 'VAT %',
                type: 'number',
                min: 0,
                max: 100,
                suffix: '%',
            },
            colSpan: 2,
        },
        {
            formItemProps: {
                name: 'usage_unit',
                label: t('usage_unit'),
                placeholder: t('usage_unit'),
                type: 'select',
                options: PRODUCT_UNITS(t),
            },
            colSpan: 4,
        },
        {
            formItemProps: {
                name: [
                    'invoice_item_apply_startdate',
                    'invoice_item_apply_enddate',
                ],
                label: t('date_time'),
                placeholder: t('date_time'),
                type: 'daterangepicker',
                endDatePresets: INVOICE_END_DATE_PRESETS,
            },
            colSpan: 4,
        },
    ]

    return (
        <Dialog
            onClose={onClose}
            open={open}
            title={
                invoice_item ? t('edit-invoice-items') : t('add-invoice-items')
            }
            closeOnClick
        >
            <DatacentralHookForm
                formControl={formControl}
                data={formData}
                onSubmit={handleSubmit}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outline',
                        handler: onClose,
                    },
                    {
                        title: t('save'),
                        type: 'submit',
                        variant: 'default',
                        disabled: isLoading,
                        loading: isLoading,
                    },
                ]}
            />
        </Dialog>
    )
}

export default AddInvoiceItems
